import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Decision, FeedbackEvent, RejectReasonCode, NiwtAgent } from '../types';
import { emitDecisionReject } from '../events';

const REASON_OPTIONS: { code: RejectReasonCode; label: string }[] = [
  { code: 'not_important_now', label: 'Not important right now' },
  { code: 'wrong_facts', label: 'Wrong facts' },
  { code: 'incomplete_context', label: 'Incomplete context' },
  { code: 'wrong_owner', label: 'Wrong owner' },
  { code: 'bad_prioritization', label: 'Bad prioritization' },
  { code: 'strategy_disagreement', label: 'Strategy disagreement' },
  { code: 'not_allowed', label: 'Not allowed / policy' },
  { code: 'other', label: 'Other' },
];

type DoInsteadAction = 'delegate' | 're_rank' | 'ask_clarifying' | 'draft_alternative' | null;

const DO_INSTEAD_OPTIONS: { value: DoInsteadAction; label: string }[] = [
  { value: null, label: '— No action' },
  { value: 'delegate', label: 'Delegate to an agent' },
  { value: 're_rank', label: 'Re-rank later' },
  { value: 'ask_clarifying', label: 'Ask clarifying question' },
  { value: 'draft_alternative', label: 'Draft alternative' },
];

interface Props {
  decision: Decision;
  agents: NiwtAgent[];
  onClose: () => void;
  onRejected: (decisionId: string) => void;
}

const RejectRecommendationModal: React.FC<Props> = ({ decision, agents, onClose, onRejected }) => {
  const [reasonCode, setReasonCode] = useState<RejectReasonCode | null>(null);
  const [doInstead, setDoInstead] = useState<DoInsteadAction>(null);
  const [delegateAgentId, setDelegateAgentId] = useState('');
  const [comment, setComment] = useState('');
  const [scope, setScope] = useState<'tenant' | 'decision_type' | 'dismiss_only'>('dismiss_only');

  const handleSubmit = () => {
    if (!reasonCode) return;

    const feedback: FeedbackEvent = {
      id: `fb-${Date.now()}`,
      decision_id: decision.id,
      reason_code: reasonCode,
      owner_comment: comment,
      do_instead: {
        action: doInstead,
        agent_id: doInstead === 'delegate' && delegateAgentId ? delegateAgentId : undefined,
      },
      scope,
      created_at: new Date().toISOString(),
    };

    emitDecisionReject(feedback);
    onRejected(decision.id);
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-base font-semibold text-gray-900">Reject Recommendation</h2>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="px-6 py-5 space-y-5">
            {/* Decision title */}
            <div className="text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
              {decision.title}
            </div>

            {/* Reason */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                Why are you rejecting this? <span className="text-red-500">*</span>
              </p>
              <div className="space-y-1.5">
                {REASON_OPTIONS.map((opt) => (
                  <label key={opt.code} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="reason"
                      value={opt.code}
                      checked={reasonCode === opt.code}
                      onChange={() => setReasonCode(opt.code)}
                      className="text-niwt-primary focus:ring-niwt-primary"
                    />
                    <span className="text-sm text-gray-700">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Do instead */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                What should Niwt do instead? <span className="text-xs text-gray-400">(optional)</span>
              </label>
              <select
                value={doInstead ?? ''}
                onChange={(e) => setDoInstead((e.target.value as DoInsteadAction) || null)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-niwt-primary"
              >
                {DO_INSTEAD_OPTIONS.map((opt) => (
                  <option key={String(opt.value)} value={opt.value ?? ''}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {doInstead === 'delegate' && (
                <select
                  value={delegateAgentId}
                  onChange={(e) => setDelegateAgentId(e.target.value)}
                  className="mt-2 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-niwt-primary"
                >
                  <option value="">Select an agent…</option>
                  {agents.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.display_name} — {a.role}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Feedback */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Feedback <span className="text-xs text-gray-400">(optional)</span>
              </label>
              <textarea
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="1–2 lines to improve future performance"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-niwt-primary"
              />
            </div>

            {/* Scope */}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-1.5">Apply feedback to</p>
              <div className="flex gap-2">
                {(
                  [
                    { value: 'dismiss_only', label: 'Dismiss only' },
                    { value: 'decision_type', label: 'This decision type' },
                    { value: 'tenant', label: 'All decisions' },
                  ] as { value: typeof scope; label: string }[]
                ).map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setScope(opt.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${
                      scope === opt.value
                        ? 'bg-niwt-primary text-white border-niwt-primary'
                        : 'bg-white text-gray-600 border-gray-300 hover:border-niwt-primary'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!reasonCode}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              Reject Recommendation
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default RejectRecommendationModal;
